// react
import { useMemo } from 'react';

// mui
import {
	alpha,
	Box,
	Button,
	Chip,
	Divider,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// types
import { Theme } from '@mui/material/styles';
import { PlanningActivity } from '../../../@types';

// icons
import {
	AccountTree as IconActivity,
	Add as IconAdd,
	Assignment as IconContract,
	SubdirectoryArrowRight as IconSubActivity,
} from '@mui/icons-material';

// components
import { CardDetail } from '../CardDetail/CardDetail';

// data
import { GRADIENTS } from '../../../data/colors';

// utils
import { formatCurrency } from '../../../utils/currency';

// design system
import { radius, spacing, typography } from '../../../utils/designSystem';

// Recursive activity row component
interface ActivityRowProps {
	activity: PlanningActivity;
	allActivities: PlanningActivity[];
	getContractsByActivity?: (activityId: number) => unknown[];
	indentLevel: number;
	onOpen: (activityId: number) => void;
	showContracts?: boolean;
	showProgress?: boolean;
	theme: Theme;
}

const ActivityRow = ({
	activity,
	allActivities,
	getContractsByActivity,
	indentLevel,
	onOpen,
	showContracts = false,
	showProgress = false,
	theme,
}: ActivityRowProps) => {
	// Find nested sub-activities for this activity
	const nestedSubActivities = useMemo(
		() =>
			allActivities.filter((a) => {
				const parentId = typeof a.parent === 'number' ? a.parent : a.parent?.id;
				return parentId === activity.id;
			}),
		[allActivities, activity.id]
	);

	const contracts = getContractsByActivity ? getContractsByActivity(activity.id) : [];
	const entityName = typeof activity.entity === 'object' ? activity.entity?.name : '';
	const progress = activity.progress || 0;
	const hasChildren = nestedSubActivities.length > 0;

	const isTopLevel = indentLevel === 0;

	return (
		<Box>
			{/* Activity Row */}
			<Stack
				direction="row"
				alignItems="center"
				spacing={isTopLevel ? spacing.sm : 1.5}
				onClick={() => onOpen(activity.id)}
				sx={{
					cursor: 'pointer',
					p: isTopLevel ? spacing.sm : 1,
					pl: isTopLevel ? spacing.sm : spacing.md,
					ml: isTopLevel ? 0 : indentLevel * 2.5,
					borderRadius: isTopLevel ? 0 : radius.sm,
					borderLeft: isTopLevel ? 'none' : '2px solid',
					borderColor: alpha(theme.palette.primary.main, 0.2),
					transition: 'all 0.15s ease',
					'&:hover': {
						bgcolor: alpha(theme.palette.primary.main, 0.05),
						...(isTopLevel ? {} : { borderColor: theme.palette.primary.main }),
					},
				}}>
				{/* Icon - circular avatar */}
				{isTopLevel ? (
					<Box
						sx={{
							alignItems: 'center',
							bgcolor: alpha(GRADIENTS.activity.icon, 0.12),
							borderRadius: '50%',
							display: 'flex',
							flexShrink: 0,
							height: { xs: 36, sm: 40 },
							justifyContent: 'center',
							width: { xs: 36, sm: 40 },
						}}>
						<IconActivity sx={{ color: GRADIENTS.activity.icon, fontSize: { xs: 18, sm: 20 } }} />
					</Box>
				) : (
					<Box
						sx={{
							alignItems: 'center',
							bgcolor: alpha(GRADIENTS.activity.icon, 0.12),
							borderRadius: '50%',
							display: 'flex',
							flexShrink: 0,
							height: { xs: 36, sm: 40 },
							justifyContent: 'center',
							width: { xs: 36, sm: 40 },
						}}>
						<IconActivity sx={{ color: GRADIENTS.activity.icon, fontSize: { xs: 18, sm: 20 } }} />
					</Box>
				)}

				{/* Name and Entity */}
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Stack direction="row" alignItems="center" spacing={1}>
						<Typography
							sx={{
								...typography.bodyBold,
								color: '#111827',
								fontSize: isTopLevel
									? { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' }
									: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
								fontWeight: 600,
							}}
							noWrap>
							{activity.name}
						</Typography>
						{!isTopLevel && hasChildren && (
							<Chip
								label={`${nestedSubActivities.length} nested`}
								size="small"
								sx={{
									bgcolor: alpha(theme.palette.grey[500], 0.12),
									color: '#374151',
									fontWeight: 600,
									height: 26,
									'& .MuiChip-label': { px: 1.5, fontSize: '0.875rem' },
								}}
							/>
						)}
					</Stack>
					<Typography
						sx={{
							...typography.body,
							color: '#6b7280',
							fontSize: isTopLevel
								? { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' }
								: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
						}}
						noWrap>
						{entityName}
					</Typography>
				</Box>

				{/* Stats */}
				<Stack
					direction="row"
					spacing={isTopLevel ? { xs: 1.5, sm: 2 } : 1.5}
					alignItems="center"
					sx={{ flexShrink: 0 }}>
					{/* Contract count (optional) */}
					{showContracts && contracts.length > 0 && (
						<Tooltip title={`${contracts.length} contract${contracts.length > 1 ? 's' : ''}`}>
							<Stack direction="row" alignItems="center" spacing={0.5}>
								<IconContract
									sx={{ color: theme.palette.info.main, fontSize: isTopLevel ? 20 : 18 }}
								/>
								<Typography
									sx={{
										...typography.body,
										color: '#374151',
										fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
									}}>
									{contracts.length}
								</Typography>
							</Stack>
						</Tooltip>
					)}

					{/* Progress bar (optional) */}
					{showProgress && (
						<Box
							sx={{
								width: { xs: 60, sm: 80 },
								height: 6,
								bgcolor: 'grey.200',
								borderRadius: 1,
								overflow: 'hidden',
							}}>
							<Box
								sx={{
									width: `${progress}%`,
									height: '100%',
									bgcolor: progress >= 100 ? theme.palette.success.main : theme.palette.info.main,
									transition: 'width 0.3s ease',
								}}
							/>
						</Box>
					)}

					{/* Amount */}
					<Box sx={{ textAlign: 'right' }}>
						<Typography
							sx={{
								...typography.value,
								color: '#111827',
								fontSize: isTopLevel
									? { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' }
									: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
								fontWeight: 700,
							}}>
							{formatCurrency({
								currency: activity.currency,
								value: activity.amount || 0,
							})}
						</Typography>
						{!!activity.currencyLocal && activity.amountLocal != null && (
							<Tooltip title={`Local currency amount (${activity.currencyLocal})`} arrow>
								<Typography
									sx={{
										color: 'text.secondary',
										fontSize: isTopLevel
											? { xs: '0.95rem', sm: '1rem', md: '1.1rem' }
											: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
										fontWeight: 500,
										lineHeight: 1.2,
										mt: 0.25,
									}}>
									{formatCurrency({
										currency: activity.currencyLocal,
										value: activity.amountLocal,
									})}
								</Typography>
							</Tooltip>
						)}
					</Box>

					{/* Status chip */}
					<Chip
						label={activity.status.replace('_', ' ')}
						size="medium"
						variant={activity.status === 'PLANNED' ? 'outlined' : 'filled'}
						color={
							activity.status === 'COMPLETED'
								? 'success'
								: activity.status === 'IN_PROGRESS'
									? 'info'
									: 'default'
						}
						sx={{
							fontWeight: 700,
							fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
							height: { xs: 26, sm: 30 },
						}}
					/>
				</Stack>
			</Stack>

			{/* Render nested sub-activities recursively */}
			{nestedSubActivities.length > 0 && (
				<Box sx={isTopLevel ? { pl: 5, pr: 2, pb: 1 } : {}}>
					<Stack spacing={0}>
						{nestedSubActivities.map((nested) => (
							<ActivityRow
								key={nested.id}
								activity={nested}
								allActivities={allActivities}
								getContractsByActivity={getContractsByActivity}
								indentLevel={indentLevel + 1}
								onOpen={onOpen}
								showContracts={showContracts}
								showProgress={showProgress}
								theme={theme}
							/>
						))}
					</Stack>
				</Box>
			)}
		</Box>
	);
};

// Main card props
interface CardActivitiesProps {
	/** List of activities to display at top level */
	activities: PlanningActivity[];
	/** All activities in the work plan/project for finding sub-activities */
	allActivities: PlanningActivity[];
	/** Empty state message */
	emptyMessage?: string;
	/** Label for the empty state action button */
	emptyActionLabel?: string;
	/** Function to get contracts for an activity */
	getContractsByActivity?: (activityId: number) => unknown[];
	/** Handler for add button click */
	onAdd?: () => void;
	/** Handler for opening an activity */
	onOpen: (activityId: number) => void;
	/** Whether to show contract counts */
	showContracts?: boolean;
	/** Whether to show progress bars */
	showProgress?: boolean;
	/** Card subtitle (e.g., "3 activities") */
	subtitle?: string;
	/** Card title */
	title?: string;
	/** Tooltip for add button */
	tooltipAdd?: string;
	/** Whether this is showing sub-activities (uses different icon) */
	variant?: 'activities' | 'subActivities';
	/** Skip the CardDetail wrapper (use when already inside a CardDetail) */
	noWrapper?: boolean;
	/** Optional theme color tint for the card (passed through to CardDetail) */
	themeColor?: string;
}

export const CardActivities = ({
	activities,
	allActivities,
	emptyActionLabel,
	emptyMessage = 'No activities found',
	getContractsByActivity,
	onAdd,
	onOpen,
	showContracts = false,
	showProgress = false,
	subtitle,
	title,
	tooltipAdd = 'Add',
	variant = 'activities',
	noWrapper = false,
	themeColor,
}: CardActivitiesProps) => {
	const theme = useTheme();

	const HeaderIcon = variant === 'subActivities' ? IconSubActivity : IconActivity;

	// Render just the content when used inside another CardDetail
	if (noWrapper) {
		if (activities.length === 0) {
			return (
				<Box sx={{ py: spacing.lg, px: spacing.md, textAlign: 'center' }}>
					<Typography
						sx={{
							color: '#6b7280',
							fontStyle: 'italic',
							fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
						}}>
						{emptyMessage}
					</Typography>
					{onAdd && emptyActionLabel && (
						<Button
							onClick={onAdd}
							size="medium"
							startIcon={<IconAdd sx={{ fontSize: 18 }} />}
							sx={{
								mt: spacing.md,
								textTransform: 'none',
								fontWeight: 600,
								fontSize: { xs: '1.125rem', sm: '1.25rem' },
								color: GRADIENTS.activity.icon,
							}}>
							{emptyActionLabel}
						</Button>
					)}
				</Box>
			);
		}

		return (
			<Stack spacing={0} divider={<Divider sx={{ borderColor: '#f3f4f6' }} />}>
				{activities.map((activity) => (
					<ActivityRow
						key={activity.id}
						activity={activity}
						allActivities={allActivities}
						getContractsByActivity={getContractsByActivity}
						indentLevel={0}
						onOpen={onOpen}
						showContracts={showContracts}
						showProgress={showProgress}
						theme={theme}
					/>
				))}
			</Stack>
		);
	}

	return (
		<CardDetail
			icon={HeaderIcon}
			title={title || 'Activities'}
			subtitle={subtitle}
			variant="activity"
			onAdd={onAdd}
			tooltipAdd={tooltipAdd}
			emptyMessage={emptyMessage}
			emptyActionLabel={emptyActionLabel}
			hideHeader={!title}
			themeColor={themeColor}
			items={activities}
			renderItem={(activity) => (
				<ActivityRow
					key={activity.id}
					activity={activity}
					allActivities={allActivities}
					getContractsByActivity={getContractsByActivity}
					indentLevel={0}
					onOpen={onOpen}
					showContracts={showContracts}
					showProgress={showProgress}
					theme={theme}
				/>
			)}
		/>
	);
};
